import uuid from 'uuid/v4'
import ScoringType from '@edulastic/constants/const/evaluationType'
import { cloneDeep, isEmpty } from 'lodash'
import { questionTitle, questionType } from '@edulastic/constants'
import { itemStructure, Q_TYPES } from '../constants'
import { AI_DOK_TO_PA_DOK_VALUES_MAP } from '../../constants'
import { getAlignmentForEduItems } from '../helpers'
import { getDefaultDataByQuestionType } from './getQuestionDefaultData'
import { getFormattedAISuggestedData } from '../../../../../AIFeatures/utils/AIQuestionAssistance'

export const replaceBlankPlaceholdersByResponseBoxes = (name, responseIds) => {
  // Replace all `{{blank}}` placeholders in the question with textinput
  // Regex matches:
  // 1. {{blank}}
  // 2. {{blankN}} (where N is one or more digits)
  // 3. ____ (underscores)
  const placeholderRegex = /\{\{blank[\w-]*}}|_{2,}/g

  let responseIdIdx = 0

  const stimulus = name.replace(placeholderRegex, (match) => {
    const responseId = responseIds[responseIdIdx]

    if (!responseId) {
      return match
    }

    responseIdIdx++

    // Return the replacement <textinput> element
    return `<textinput responseindex="${responseIdIdx}" id="${responseId}" key="${responseId}" contenteditable="false"></textinput>`
  })

  return [stimulus, responseIdIdx]
}

export const processClozeText = (
  question,
  alignmentData,
  grades,
  subjects,
  aiDocExtracted = false
) => {
  const { type, title, ...restData } = cloneDeep(
    getDefaultDataByQuestionType(Q_TYPES.CLOZE_TEXT)
  )

  const item = cloneDeep(itemStructure)
  if (aiDocExtracted) {
    item.aiDocExtracted = true
  }

  const alignment = !isEmpty(question.autoGeneratedQuestionAlignment?.[0])
    ? question.autoGeneratedQuestionAlignment
    : getAlignmentForEduItems(alignmentData, question.commonCoreStandard)

  const aiSuggestedMetadata = getFormattedAISuggestedData({
    standards: question?.autoGeneratedQuestionAlignment?.[0]?.standards || [],
    dok: AI_DOK_TO_PA_DOK_VALUES_MAP[question?.depthOfKnowledge] || null,
    difficulty: question?.difficultLevel || null,
  })

  item._id = uuid()
  const qId = uuid()

  const [row] = item.rows

  const widget = {
    widgetType: 'question',
    title: questionTitle.CLOZE_TEXT,
    type: questionType.CLOZE_TEXT,
    reference: qId,
    tabIndex: 0,
  }
  row.widgets.push(widget)

  const responseIds = question.options.map(() => uuid())

  const [stimulus, numberOfBlanks] = replaceBlankPlaceholdersByResponseBoxes(
    question.name,
    responseIds
  )

  const validation = {
    scoringType: ScoringType.EXACT_MATCH,
    mixAndMatch: true,
    ignoreCase: true,
    validResponse: {
      score: 1,
      value: question.options.slice(0, numberOfBlanks).map((option, index) => ({
        value: option.name,
        index,
        id: responseIds[index],
      })),
    },
    altResponses: [],
  }

  const clozeTextQuestion = {
    ...restData,
    id: qId,
    title,
    type,
    stimulus,
    uiStyle: {
      fontsize: 'normal',
      heightpx: 32,
      placeholder: '',
      responsecontainerindividuals: [],
      responsecontainerposition: 'bottom',
      stemNumeration: '',
      widthpx: 140,
    },
    validation,
    hints: [],
    responseIds: responseIds.map((id, index) => ({
      id,
      index,
    })),
    itemScore: 1,
    alignment,
    grades,
    subjects,
    ...(AI_DOK_TO_PA_DOK_VALUES_MAP[question.depthOfKnowledge]
      ? {
          depthOfKnowledge:
            AI_DOK_TO_PA_DOK_VALUES_MAP[question.depthOfKnowledge],
        }
      : {}),
    authorDifficulty: question.difficultLevel,
    ...(!isEmpty(aiSuggestedMetadata) ? { aiSuggestedMetadata } : {}),
  }

  return {
    ...item,
    data: {
      questions: [clozeTextQuestion],
      resources: [],
    },
  }
}
